import React, { useEffect, useMemo, useState } from 'react'
import { VictoryAxis, VictoryChart, VictoryLine, VictoryTooltip, VictoryVoronoiContainer } from 'victory'

type XValue = number | string | Date
export type LineChartPoint = { x: XValue; y: number; seriesKey?: string; seriesName?: string }
export type LineChartSeries = {
  key: string
  name: string
  color: string
  data: LineChartPoint[]
}

type Props = {
  data: LineChartPoint[]
  series?: LineChartSeries[]
  width?: number
  height?: number
  strokeColor?: string
  animate?: boolean
  xTickValues?: XValue[]
  xTickFormat?: ((tick: XValue) => string) | string[]
  yTickFormat?: ((tick: XValue) => string) | string[]
  tooltipFormatter?: (point: LineChartPoint) => string
}

const LineChart: React.FC<Props> = ({
  data,
  series,
  width = 960,
  height = 540,
  strokeColor = '#009ef7',
  animate = true,
  xTickValues,
  xTickFormat,
  yTickFormat,
  tooltipFormatter
}) => {
  const fontFamily = 'Poppins, Helvetica, sans-serif'
  const gridColor = '#e9ecef'
  const formatTooltip = tooltipFormatter ?? ((point: LineChartPoint) => point.y.toString())
  const [hoveredSeriesKey, setHoveredSeriesKey] = useState<string | null>(null)
  const [selectedSeriesKey, setSelectedSeriesKey] = useState<string | null>(null)
  const visibleSeries = useMemo(() => (series ?? []).filter((seriesItem) => seriesItem.data.length > 0), [series])
  const useMultiSeries = visibleSeries.length > 0
  const activeSeriesKey = hoveredSeriesKey ?? selectedSeriesKey

  useEffect(() => {
    if (!useMultiSeries) {
      setSelectedSeriesKey(null)
      return
    }

    setSelectedSeriesKey((currentSeriesKey) => {
      if (currentSeriesKey && visibleSeries.some((seriesItem) => seriesItem.key === currentSeriesKey)) {
        return currentSeriesKey
      }

      const highestSeries = visibleSeries.reduce<LineChartSeries | null>((currentHighest, seriesItem) => {
        const seriesAverage =
          seriesItem.data.reduce((total, point) => total + point.y, 0) / Math.max(seriesItem.data.length, 1)
        if (currentHighest === null) {
          return seriesItem
        }

        const currentAverage =
          currentHighest.data.reduce((total, point) => total + point.y, 0) / Math.max(currentHighest.data.length, 1)
        return seriesAverage > currentAverage ? seriesItem : currentHighest
      }, null)

      return highestSeries?.key ?? null
    })
  }, [useMultiSeries, visibleSeries])

  return (
    <>
      <VictoryChart
        animate={animate ? { duration: 1000, onLoad: { duration: 1 } } : false}
        width={width}
        height={height}
        containerComponent={
          <VictoryVoronoiContainer
            labels={({ datum }) => formatTooltip(datum as LineChartPoint)}
            labelComponent={
              <VictoryTooltip
                style={{ fontFamily: fontFamily, fontSize: 10 }}
                flyoutStyle={{ stroke: '#dfe2e6', fill: '#fff' }}
              />
            }
          />
        }>
        {useMultiSeries ? (
          visibleSeries.map((seriesItem) => (
            <VictoryLine
              key={seriesItem.key}
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onClick: () => {
                      setSelectedSeriesKey(seriesItem.key)
                      return []
                    },
                  },
                },
              ]}
              style={{
                data: {
                  stroke: seriesItem.color,
                  strokeWidth: activeSeriesKey === seriesItem.key ? 4 : 2,
                  opacity: activeSeriesKey === null || activeSeriesKey === seriesItem.key ? 1 : 0.2,
                },
              }}
              data={seriesItem.data}
            />
          ))
        ) : (
          <VictoryLine style={{ data: { stroke: strokeColor } }} data={data} />
        )}
        <VictoryAxis
          tickValues={xTickValues}
          tickFormat={xTickFormat}
          style={{ tickLabels: { fontFamily: fontFamily, fontSize: 12 } }}
          animate={false}
        />
        <VictoryAxis
          dependentAxis
          animate={false}
          tickFormat={yTickFormat}
          style={{
            tickLabels: { fontFamily: fontFamily, fontSize: 12 },
            grid: { stroke: gridColor, strokeDasharray: '10,5' }
          }}
        />
      </VictoryChart>
      {useMultiSeries && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginTop: 0,
            paddingBottom: 8,
          }}
        >
          {visibleSeries.map((seriesItem) => (
            <button
              key={seriesItem.key}
              type='button'
              onClick={() => setSelectedSeriesKey(seriesItem.key)}
              onMouseEnter={() => setHoveredSeriesKey(seriesItem.key)}
              onMouseLeave={() => setHoveredSeriesKey(null)}
              onFocus={() => setHoveredSeriesKey(seriesItem.key)}
              onBlur={() => setHoveredSeriesKey(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid #dbe2e8',
                borderRadius: 999,
                padding: '6px 10px',
                background: 'transparent',
                color: '#3f4254',
                fontSize: 12,
                cursor: 'pointer',
                opacity: activeSeriesKey === null || activeSeriesKey === seriesItem.key ? 1 : 0.45,
                borderColor: selectedSeriesKey === seriesItem.key ? seriesItem.color : '#dbe2e8',
                transition: 'opacity 0.15s ease, border-color 0.15s ease',
              }}
              title={seriesItem.name}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: seriesItem.color,
                  display: 'inline-block',
                }}
              />
              <span>{seriesItem.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  )
}
export { LineChart }
